import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc, Timestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

interface StoryData {
  title: string;
  content: {
    chapters: Array<{
      id: number;
      title: string;
      content: string;
    }>;
  };
  userId: string;
  submittedAt: Date | null;
  assignmentId?: string;  // ← ADD THIS
  assignmentTitle?: string;  // ← ADD THIS
}

interface StudentData {
  displayName: string;
  email: string;
}

const StoryViewer: React.FC = () => {
  const navigate = useNavigate();
  const { classId, storyId } = useParams<{ classId: string; storyId: string }>();
  const { currentUser } = useAuth();
  const [story, setStory] = useState<StoryData | null>(null);
  const [student, setStudent] = useState<StudentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [publicFeedback, setPublicFeedback] = useState('');
const [privateNotes, setPrivateNotes] = useState('');
const [reactions, setReactions] = useState({ clap: false, heart: false, sparkle: false });
const [existingFeedback, setExistingFeedback] = useState<any>(null);
const [saving, setSaving] = useState(false);
const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
const [feedbackSaved, setFeedbackSaved] = useState(false);
const [showReturnConfirm, setShowReturnConfirm] = useState(false);

  useEffect(() => {
    const loadStoryAndStudent = async () => {
      if (!currentUser || !classId || !storyId) return;

      try {
        // Verify teacher owns this class
        const classDoc = await getDoc(doc(db, 'classes', classId));
        if (!classDoc.exists() || classDoc.data().teacherId !== currentUser.uid) {
          alert('Access denied');
          navigate('/teacher');
          return;
        }

        // Load story
        const storyDoc = await getDoc(doc(db, 'stories', storyId));
        if (!storyDoc.exists()) {
          alert('Story not found');
          navigate(`/teacher/class/${classId}`);
          return;
        }

        const storyData = storyDoc.data();

setStory({
  title: storyData.title || 'Untitled Story',
  content: storyData.content || { chapters: [] },
  userId: storyData.userId,
  submittedAt: storyData.submittedAt?.toDate() || null,
  assignmentId: storyData.assignmentId,  // ← ADD THIS
  assignmentTitle: storyData.assignmentTitle  // ← ADD THIS TOO
});

        // Load student info
        const studentDoc = await getDoc(doc(db, 'users', storyData.userId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          setStudent({
            displayName: studentData.displayName || 'Student',
            email: studentData.email || ''
          });
        }
      } catch (error) {
        console.error('Error loading story:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStoryAndStudent();
  }, [currentUser, classId, storyId, navigate]);

  useEffect(() => {
    const loadFeedback = async () => {
      if (!storyId) return;
      
      try {
        const feedbackDoc = await getDoc(doc(db, 'teacherFeedback', storyId));
        if (feedbackDoc.exists()) {
          const data = feedbackDoc.data();
          
          setExistingFeedback(data);
          setPublicFeedback(data.publicComment || '');
          setPrivateNotes(data.privateNotes || '');
          setReactions(data.reactions || { clap: false, heart: false, sparkle: false });
          setSelectedSuggestions(data.suggestions || []);
        }
      } catch (error) {
        console.error('Error loading feedback:', error);
      }
    };
    
    loadFeedback();
  }, [storyId]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: "'Inter', sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '16px' }}>📚</div>
          <div style={{ fontSize: '1.2rem', color: '#666' }}>Loading story...</div>
        </div>
      </div>
    );
  }

  if (!story || !student) return null;

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: '#f5f5f5',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '2px solid #E0E0E0',
        padding: '20px 40px'
      }}>
        <button
          onClick={() => navigate(`/teacher/class/${classId}/student/${story.userId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#6C5CE7',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          ← Back to {student.displayName}'s Work
        </button>
        
        <h1 style={{
          fontSize: '1.8rem',
          fontWeight: '800',
          color: '#333',
          margin: '0 0 8px 0'
        }}>
          {story.title}
        </h1>
        
        <div style={{
          fontSize: '0.9rem',
          color: '#666'
        }}>
          By {student.displayName} • Submitted {story.submittedAt ? new Date(story.submittedAt).toLocaleDateString() : 'Recently'}
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flex: 1,
        maxWidth: '1400px',
        width: '100%',
        margin: '0 auto',
        gap: '24px',
        padding: '24px'
      }}>
        {/* Story Content (scrollable) */}
        <div style={{
          flex: 1,
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 200px)'
        }}>
          {story.content.chapters.map((chapter: any, idx: number) => (
    <div key={chapter.id || idx} style={{ marginBottom: '32px' }}>
        <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#333'
        }}>
            {chapter.title}
        </h2>
        {/* Block-based format */}
        {chapter.blocks ? (
            chapter.blocks.map((block: any) => {
                if (block.type === 'text') {
                    return (
                        <div
                            key={block.id}
                            style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', lineHeight: '1.8', color: '#333', marginBottom: '16px' }}
                            dangerouslySetInnerHTML={{ __html: block.content || '' }}
                        />
                    );
                }
                if (block.type === 'image') {
                    return (
                        <div key={block.id} style={{ textAlign: 'center', margin: '24px 0' }}>
                            <img src={block.imageUrl} alt={block.caption || ''} style={{ maxWidth: '100%', borderRadius: '8px' }} />
                            {block.caption && <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>{block.caption}</div>}
                        </div>
                    );
                }
                return null;
            })
        ) : (
            /* Legacy format */
            <div
                style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', lineHeight: '1.8', color: '#333' }}
                dangerouslySetInnerHTML={{ __html: chapter.content || '' }}
            />
        )}
    </div>
))}
        </div>

        {/* Feedback Panel (fixed) */}
        <div style={{
          width: '350px',
          flexShrink: 0
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            position: 'sticky',
            top: '24px'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              marginBottom: '20px',
              color: '#333'
            }}>
              Feedback
            </h3>

            {/* Public Feedback */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#666',
                marginBottom: '8px'
              }}>
                Public Feedback
              </label>
              <textarea
  value={publicFeedback}
  onChange={(e) => setPublicFeedback(e.target.value)}
  placeholder="Write feedback visible to student..."
  style={{
    width: '100%',
    minHeight: '100px',
    padding: '12px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontFamily: "'Inter', sans-serif",
    resize: 'vertical',
    boxSizing: 'border-box'
  }}
/>
            </div>

            {/* Quick Reactions */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#666',
                marginBottom: '12px'
              }}>
                Quick Reactions
              </label>
              <div style={{
  display: 'flex',
  gap: '12px'
}}>
  {[
    { emoji: '👏', key: 'clap' },
    { emoji: '❤️', key: 'heart' },
    { emoji: '✨', key: 'sparkle' }
  ].map(({ emoji, key }) => (
    <button
      key={key}
      onClick={() => setReactions(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))}
      style={{
        fontSize: '2rem',
        padding: '12px',
        border: `2px solid ${reactions[key as keyof typeof reactions] ? '#6C5CE7' : '#E0E0E0'}`,
        borderRadius: '8px',
        background: reactions[key as keyof typeof reactions] ? '#F3F0FF' : 'white',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      {emoji}
    </button>
  ))}
</div>
            </div>

            {/* Suggestions */}
            <div style={{ marginBottom: '24px' }}>
  <label style={{
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#666',
    marginBottom: '12px'
  }}>
    Quick Suggestions
  </label>
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  }}>
    {[
      'Try adding more sensory detail here',
      'What happened next?',
      'Describe the setting more',
      'Can you show instead of tell?',
      'Great start! Keep developing this idea',
      'Check your spelling and punctuation'
    ].map((suggestion, idx) => (
      <button
        key={idx}
        onClick={() => {
          if (selectedSuggestions.includes(suggestion)) {
            setSelectedSuggestions(prev => prev.filter(s => s !== suggestion));
          } else {
            setSelectedSuggestions(prev => [...prev, suggestion]);
          }
        }}
        style={{
          padding: '10px 12px',
          background: selectedSuggestions.includes(suggestion) ? '#6C5CE7' : '#F3F0FF',
          border: `2px solid #6C5CE7`,
          borderRadius: '8px',
          fontSize: '0.85rem',
          fontWeight: '600',
          color: selectedSuggestions.includes(suggestion) ? 'white' : '#6C5CE7',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'all 0.2s'
        }}
      >
        {selectedSuggestions.includes(suggestion) ? '✓ ' : '+ '}
        {suggestion}
      </button>
    ))}
  </div>
</div>
            {/* Teacher Notes */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#666',
                marginBottom: '8px'
              }}>
                Teacher Notes (Private)
              </label>
              <textarea
  value={privateNotes}  // ← Make sure this is here!
  onChange={(e) => setPrivateNotes(e.target.value)}
  placeholder="Private notes only you can see..."
  style={{
    width: '100%',
    minHeight: '80px',
    padding: '12px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontFamily: "'Inter', sans-serif",
    resize: 'vertical',
    boxSizing: 'border-box'
  }}
/>
            </div>

            {/* Action Buttons */}
            {feedbackSaved && (
    <div style={{ background: '#d1fae5', color: '#006947', padding: '10px 14px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '700', textAlign: 'center', marginBottom: '8px' }}>
        ✅ Feedback saved! Student can see your comments.
    </div>
)}

<button
    onClick={async () => {
        if (!currentUser || !storyId) return;
        setSaving(true);
        try {
            const feedbackData = {
                teacherId: currentUser.uid,
                teacherName: currentUser.displayName || 'Teacher',
                publicComment: publicFeedback,
                privateNotes: privateNotes,
                reactions: reactions,
                suggestions: selectedSuggestions,
                status: 'submitted',
                timestamp: Timestamp.now(),
                updatedAt: Timestamp.now()
            };
            await setDoc(doc(db, 'teacherFeedback', storyId), feedbackData);
            if (story.assignmentId && story.userId) {
                await setDoc(
                    doc(db, 'studentAssignments', `${story.userId}_${story.assignmentId}`),
                    { hasFeedback: true },
                    { merge: true }
                );
            }
            setFeedbackSaved(true);
            setTimeout(() => setFeedbackSaved(false), 3000);
        } catch (error) {
            console.error('Error saving feedback:', error);
        } finally {
            setSaving(false);
        }
    }}
    disabled={saving}
    style={{ width: '100%', padding: '14px', background: saving ? '#ccc' : '#6C5CE7', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}
>
    {saving ? '💾 Saving...' : '✅ Save Feedback'}
</button>

<div style={{ fontSize: '0.85rem', color: '#666', textAlign: 'center', fontStyle: 'italic', margin: '4px 0 4px 0' }}>
    Saves feedback without returning the story
</div>

<button
    onClick={() => setShowReturnConfirm(true)}
    style={{ width: '100%', padding: '14px', background: '#F59E0B', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer' }}
>
    🔄 Return for Revision
</button>
          </div> {/* closes sticky feedback panel inner div */}
        </div> {/* closes feedback panel outer div */}
      </div> {/* closes main content flex div */}

        {/* Return confirmation modal */}
        {showReturnConfirm && (
            <div onClick={() => setShowReturnConfirm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px', backdropFilter: 'blur(4px)' }}>
                <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', padding: '32px', maxWidth: '400px', width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}>🔄</div>
                    <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: '#252f3c', margin: '0 0 10px 0' }}>Return for Revision?</h3>
                    <p style={{ fontSize: '0.875rem', color: '#525c6a', margin: '0 0 24px 0', lineHeight: '1.6' }}>This will return the story to the student. They'll see your feedback and can resubmit when ready.</p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button onClick={() => setShowReturnConfirm(false)} style={{ flex: 1, padding: '12px', background: '#fff', color: '#525c6a', border: '1.5px solid #a4aebe', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={async () => {
                            try {
                                const feedbackData = { teacherId: currentUser!.uid, teacherName: currentUser!.displayName || 'Teacher', publicComment: publicFeedback, privateNotes, reactions, suggestions: selectedSuggestions, status: 'returned', timestamp: Timestamp.now(), updatedAt: Timestamp.now() };
                                await setDoc(doc(db, 'teacherFeedback', storyId!), feedbackData);
                                await updateDoc(doc(db, 'stories', storyId!), { status: 'returned', returnedAt: Timestamp.now() });
                                if (story.assignmentId && story.userId) {
                                    await setDoc(doc(db, 'studentAssignments', `${story.userId}_${story.assignmentId}`), { status: 'returned', returnedAt: Timestamp.now() }, { merge: true });
                                }
                                navigate(`/teacher/class/${classId}`);
                            } catch (error) { console.error('Error returning story:', error); }
                        }} style={{ flex: 1, padding: '12px', background: '#F59E0B', color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer' }}>
                            Yes, Return
                        </button>
                    </div>
                </div>
            </div>
        )}

    </div> 
  );
};

export default StoryViewer;